import { StyleMetadataEngine } from '../abstractStyles/StyleMetadataEngine';
import type { AtomicValueWithNesting } from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';

/**
 * Configuration for a single property to extract from style metadata
 */
export type PropertyConfig<
  TResultKey extends string = string,
  TMixedKey extends string = string,
> = {
  resultKey: TResultKey;
  mixedKey: TMixedKey;
  atomicKeys: string | readonly string[];
};

/**
 * Infers the result type from an array of property configurations
 */
export type ProcessedStyleMetadata<
  TConfig extends readonly PropertyConfig<string, string>[],
> = {
  [K in TConfig[number] as K['resultKey']]: string[];
} & {
  [K in TConfig[number] as K['mixedKey']]: boolean;
};

/**
 * Extracts atomic values from decomposed style properties.
 * Uses resolvedValue to get the actual CSS variable resolved value.
 */
const getAtomicValues = (
  decomposed: Record<string, AtomicValueWithNesting[]>,
  atomicKeys: string | readonly string[],
): string[] => {
  const keys = Array.isArray(atomicKeys) ? atomicKeys : [atomicKeys];
  const allValues: string[] = [];

  for (const key of keys) {
    const values = decomposed[key];
    if (values !== undefined) {
      // Use resolvedValue instead of value (resolves CSS variables)
      allValues.push(...values.map((value) => value.resolvedValue));
    }
  }

  return allValues;
};

/**
 * Analyzes values to determine if they are mixed or uniform.
 * Always returns a string[]; the isMixed flag carries the uniform/mixed distinction.
 * Deduplicates uniform values to a single-element array.
 */
const analyzeValues = (
  values: string[],
): { value: string[]; isMixed: boolean } => {
  if (values.length === 0) {
    return { value: [], isMixed: false };
  }

  const allSame = values.every((v) => v === values[0]);

  if (allSame) {
    return { value: [values[0]], isMixed: false };
  } else {
    return { value: values, isMixed: true };
  }
};

/**
 * Generic function to process style metadata for lobby items.
 * Engine is the single source of truth - no need to pass separate metadata.
 *
 * @param engine - StyleMetadataEngine containing all style data
 * @param properties - Array of property configurations defining what to extract
 * @returns Object containing extracted values and mixed state flags (fully typed based on config)
 */
export const processStyleMetadataForLobby = <
  TConfig extends readonly PropertyConfig<string, string>[],
>(
  engine: StyleMetadataEngine | undefined,
  properties: TConfig,
): ProcessedStyleMetadata<TConfig> => {
  if (!engine) {
    return Object.fromEntries([
      ...properties.map((prop) => [prop.resultKey, []]),
      ...properties.map((prop) => [prop.mixedKey, false]),
    ]) as ProcessedStyleMetadata<TConfig>;
  }

  const aggregatedValues = engine.getAllValues();

  let decomposed: Record<string, AtomicValueWithNesting[]> = {};

  if (aggregatedValues.mode === 'cssProperties') {
    decomposed = aggregatedValues.decomposed.cssProperties;
  } else if (aggregatedValues.mode === 'cssCustomProperties') {
    decomposed = aggregatedValues.decomposed.cssCustomProperties;
  } else if (aggregatedValues.mode === 'mixed') {
    decomposed = {
      ...aggregatedValues.decomposed.cssCustomProperties,
      ...aggregatedValues.decomposed.cssProperties,
    };
  }

  return Object.fromEntries(
    properties.flatMap((property) => {
      const values = getAtomicValues(decomposed, property.atomicKeys);
      const analysis = analyzeValues(values);
      return [
        [property.resultKey, analysis.value],
        [property.mixedKey, analysis.isMixed],
      ];
    }),
  ) as ProcessedStyleMetadata<TConfig>;
};
